/**
 * ArchetypeQuiz — 3-question self-identification quiz on the landing.
 *
 * Goal: solve the "who is this for?" bottleneck on a multi-persona
 * landing. After 3 quick taps the visitor lands on /for/:slug — their
 * dedicated landing — instead of bouncing because the generic page
 * doesn't match their context.
 *
 * Scoring matrix
 * --------------
 *   Each answer adds points to 1-2 personas. After 3 questions we pick
 *   the persona with the highest total score (ties broken by the order
 *   they were declared, so the first persona wins).
 *
 * Personas (matches keys + slugs from landingArchetypeContent.js):
 *   developer, designer, content-creator, consultant,
 *   lawyer-accountant, coach-tutor, ecommerce, photographer.
 */
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
  ArrowRight, ArrowUpRight, Sparkles, RotateCcw, Check,
  Code2, Palette, Video, Briefcase, Scale, GraduationCap,
  ShoppingBag, Camera,
} from "lucide-react";

const ICON_BY_SLUG = {
  "developer": Code2,
  "designer": Palette,
  "content-creator": Video,
  "consultant": Briefcase,
  "lawyer-accountant": Scale,
  "coach-tutor": GraduationCap,
  "ecommerce": ShoppingBag,
  "photographer": Camera,
};

const PERSONA_LABEL = {
  es: {
    "developer": "Desarrollador/a freelance",
    "designer": "Diseñador/a UX/UI",
    "content-creator": "Content creator",
    "consultant": "Consultor/a independiente",
    "lawyer-accountant": "Abogado/a o contador/a",
    "coach-tutor": "Coach o tutor/a online",
    "ecommerce": "E-commerce solo",
    "photographer": "Fotógrafo/a o eventos",
  },
  en: {
    "developer": "Freelance developer",
    "designer": "UX/UI designer",
    "content-creator": "Content creator",
    "consultant": "Independent consultant",
    "lawyer-accountant": "Lawyer or accountant",
    "coach-tutor": "Online coach or tutor",
    "ecommerce": "Solo e-commerce",
    "photographer": "Photographer / events",
  },
  pt: {
    "developer": "Desenvolvedor/a freelance",
    "designer": "Designer UX/UI",
    "content-creator": "Criador de conteúdo",
    "consultant": "Consultor/a independente",
    "lawyer-accountant": "Advogado/a ou contador/a",
    "coach-tutor": "Coach ou tutor online",
    "ecommerce": "E-commerce solo",
    "photographer": "Fotógrafo/a ou eventos",
  },
};

// Each option contributes weights to one or more personas. Designed so
// the dominant answer per question maps to ~1 strong persona + a
// secondary tilt — gives a more confident match after 3 questions.
const QUIZ = {
  es: [
    {
      q: "¿Cómo te pagan tus clientes?",
      options: [
        { label: "Cliente del exterior, en USD/EUR", weights: { "developer": 3, "content-creator": 1 } },
        { label: "Anticipo + hitos por proyecto", weights: { "designer": 3, "photographer": 1 } },
        { label: "Suscripciones mensuales", weights: { "coach-tutor": 3, "content-creator": 1 } },
        { label: "Retainer fijo + algo de spot", weights: { "consultant": 3 } },
        { label: "Honorarios cuando salen / al cierre", weights: { "lawyer-accountant": 3 } },
        { label: "Mercado Libre / Shopify / redes", weights: { "ecommerce": 3 } },
        { label: "Eventos por temporada", weights: { "photographer": 3 } },
        { label: "Varias plataformas distintas", weights: { "content-creator": 3, "coach-tutor": 1 } },
      ],
    },
    {
      q: "¿Cuál es tu mayor dolor con la plata?",
      options: [
        { label: "FX entre USD y mi moneda local", weights: { "developer": 3, "ecommerce": 1 } },
        { label: "Mes gordo / mes flaco", weights: { "designer": 2, "photographer": 2 } },
        { label: "Bajas silenciosas / churn", weights: { "coach-tutor": 3 } },
        { label: "Cobros con probabilidad incierta", weights: { "consultant": 2, "content-creator": 1, "designer": 1 } },
        { label: "Plata bloqueada por la plataforma", weights: { "ecommerce": 3 } },
        { label: "Honorarios impredecibles", weights: { "lawyer-accountant": 3 } },
        { label: "Vivir 12 meses con cash de 5", weights: { "photographer": 3 } },
        { label: "Muchas fuentes, ningún resumen", weights: { "content-creator": 3 } },
      ],
    },
    {
      q: "¿Qué quieres saber primero al abrir Lumfi un lunes?",
      options: [
        { label: "Cuánta plata real tengo (sin FX confuso)", weights: { "developer": 3 } },
        { label: "Cuánto puedo gastar esta semana", weights: { "designer": 2, "consultant": 1, "photographer": 1 } },
        { label: "Mi MRR real después de fees", weights: { "coach-tutor": 3 } },
        { label: "Qué pasa si pierdo el retainer", weights: { "consultant": 3 } },
        { label: "Qué cobros vienen y cuáles se atrasan", weights: { "lawyer-accountant": 2, "content-creator": 2 } },
        { label: "Si la próxima semana se queda sin caja", weights: { "ecommerce": 3 } },
        { label: "Si voy bien para el mes flaco", weights: { "photographer": 3 } },
        { label: "Mi resumen consolidado en un mensaje", weights: { "content-creator": 3 } },
      ],
    },
  ],
  en: [
    {
      q: "How do your clients pay you?",
      options: [
        { label: "International clients, in USD/EUR", weights: { "developer": 3, "content-creator": 1 } },
        { label: "Upfront + milestones per project", weights: { "designer": 3, "photographer": 1 } },
        { label: "Monthly subscriptions", weights: { "coach-tutor": 3, "content-creator": 1 } },
        { label: "Fixed retainer + some spot work", weights: { "consultant": 3 } },
        { label: "Fees when they clear / at close", weights: { "lawyer-accountant": 3 } },
        { label: "Mercado Libre / Shopify / social", weights: { "ecommerce": 3 } },
        { label: "Seasonal events", weights: { "photographer": 3 } },
        { label: "Several different platforms", weights: { "content-creator": 3, "coach-tutor": 1 } },
      ],
    },
    {
      q: "What's your biggest cash pain?",
      options: [
        { label: "FX between USD and local currency", weights: { "developer": 3, "ecommerce": 1 } },
        { label: "Fat month / thin month", weights: { "designer": 2, "photographer": 2 } },
        { label: "Silent churn", weights: { "coach-tutor": 3 } },
        { label: "Uncertain probability of payments", weights: { "consultant": 2, "content-creator": 1, "designer": 1 } },
        { label: "Cash locked by the platform", weights: { "ecommerce": 3 } },
        { label: "Unpredictable fees", weights: { "lawyer-accountant": 3 } },
        { label: "Living 12 months on 5 months of cash", weights: { "photographer": 3 } },
        { label: "Many sources, no single summary", weights: { "content-creator": 3 } },
      ],
    },
    {
      q: "What do you want to know first when you open Lumfi on a Monday?",
      options: [
        { label: "How much real cash do I have (no FX confusion)", weights: { "developer": 3 } },
        { label: "How much I can spend this week", weights: { "designer": 2, "consultant": 1, "photographer": 1 } },
        { label: "My real MRR after fees", weights: { "coach-tutor": 3 } },
        { label: "What happens if I lose the retainer", weights: { "consultant": 3 } },
        { label: "What's coming in and what's late", weights: { "lawyer-accountant": 2, "content-creator": 2 } },
        { label: "If next week runs out of cash", weights: { "ecommerce": 3 } },
        { label: "If I'm on track for the slow months", weights: { "photographer": 3 } },
        { label: "One consolidated digest", weights: { "content-creator": 3 } },
      ],
    },
  ],
  pt: [
    {
      q: "Como seus clientes pagam?",
      options: [
        { label: "Cliente do exterior, em USD/EUR", weights: { "developer": 3, "content-creator": 1 } },
        { label: "Sinal + marcos por projeto", weights: { "designer": 3, "photographer": 1 } },
        { label: "Assinaturas mensais", weights: { "coach-tutor": 3, "content-creator": 1 } },
        { label: "Retainer fixo + algum projeto solto", weights: { "consultant": 3 } },
        { label: "Honorários quando saem / no fechamento", weights: { "lawyer-accountant": 3 } },
        { label: "Mercado Livre / Shopify / redes", weights: { "ecommerce": 3 } },
        { label: "Eventos por temporada", weights: { "photographer": 3 } },
        { label: "Várias plataformas diferentes", weights: { "content-creator": 3, "coach-tutor": 1 } },
      ],
    },
    {
      q: "Qual é a sua maior dor com o dinheiro?",
      options: [
        { label: "Câmbio entre USD e moeda local", weights: { "developer": 3, "ecommerce": 1 } },
        { label: "Mês gordo / mês fraco", weights: { "designer": 2, "photographer": 2 } },
        { label: "Cancelamentos silenciosos", weights: { "coach-tutor": 3 } },
        { label: "Probabilidade incerta dos recebíveis", weights: { "consultant": 2, "content-creator": 1, "designer": 1 } },
        { label: "Dinheiro travado pela plataforma", weights: { "ecommerce": 3 } },
        { label: "Honorários imprevisíveis", weights: { "lawyer-accountant": 3 } },
        { label: "Viver 12 meses com caixa de 5", weights: { "photographer": 3 } },
        { label: "Muitas fontes, nenhum resumo", weights: { "content-creator": 3 } },
      ],
    },
    {
      q: "O que você quer saber primeiro ao abrir Lumfi numa segunda?",
      options: [
        { label: "Quanto caixa real eu tenho", weights: { "developer": 3 } },
        { label: "Quanto posso gastar essa semana", weights: { "designer": 2, "consultant": 1, "photographer": 1 } },
        { label: "Meu MRR real após fees", weights: { "coach-tutor": 3 } },
        { label: "O que acontece se eu perder o retainer", weights: { "consultant": 3 } },
        { label: "Que recebíveis vêm e quais atrasam", weights: { "lawyer-accountant": 2, "content-creator": 2 } },
        { label: "Se a próxima semana fica sem caixa", weights: { "ecommerce": 3 } },
        { label: "Se estou bem para os meses fracos", weights: { "photographer": 3 } },
        { label: "Um resumo consolidado", weights: { "content-creator": 3 } },
      ],
    },
  ],
};

function computeMatch(answers) {
  const scores = {
    "developer": 0, "designer": 0, "content-creator": 0, "consultant": 0,
    "lawyer-accountant": 0, "coach-tutor": 0, "ecommerce": 0, "photographer": 0,
  };
  for (const opt of answers) {
    if (!opt) continue;
    for (const [k, v] of Object.entries(opt.weights)) {
      scores[k] = (scores[k] || 0) + v;
    }
  }
  let best = null;
  let bestScore = -1;
  for (const [slug, score] of Object.entries(scores)) {
    if (score > bestScore) {
      best = slug;
      bestScore = score;
    }
  }
  return { slug: best, score: bestScore, all: scores };
}

export function ArchetypeQuiz({ lang = "es" }) {
  const set = QUIZ[lang] || QUIZ.es;
  const labels = PERSONA_LABEL[lang] || PERSONA_LABEL.es;
  const isEs = lang === "es";
  const isEn = lang === "en";

  const [step, setStep] = useState(0); // 0..3, 3 = result
  const [answers, setAnswers] = useState([null, null, null]);

  const result = useMemo(() => (step === 3 ? computeMatch(answers) : null), [step, answers]);

  const reset = () => {
    setAnswers([null, null, null]);
    setStep(0);
  };

  const pickOption = (opt) => {
    const next = [...answers];
    next[step] = opt;
    setAnswers(next);
    setStep(step + 1);
  };

  const progress = (Math.min(step, 3) / 3) * 100;

  return (
    <section
      id="quiz"
      data-testid="landing-archetype-quiz"
      className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24"
    >
      <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-emerald-700">
          <span className="h-px w-6 bg-current opacity-60" />
          {isEs ? "Auto-diagnóstico" : isEn ? "Self-quiz" : "Auto-diagnóstico"}
        </div>
        <h2 className="font-serif-display mt-4 sm:mt-5 text-3xl sm:text-4xl lg:text-5xl leading-tight font-medium">
          {isEs
            ? "¿Cuál Lumfi es para tú?"
            : isEn
            ? "Which Lumfi is for you?"
            : "Qual Lumfi é para você?"}
        </h2>
        <p className="mt-4 text-slate-600 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
          {isEs
            ? "3 preguntas, 30 segundos. Te llevamos directo a tu landing."
            : isEn
            ? "3 questions, 30 seconds. We take you straight to your landing."
            : "3 perguntas, 30 segundos. Te levamos direto para sua landing."}
        </p>
      </div>

      <div className="max-w-2xl mx-auto relative bg-slate-900 text-white rounded-3xl p-6 sm:p-10 overflow-hidden border border-emerald-500/15">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 grain-overlay opacity-30 pointer-events-none" />

        {/* Progress bar */}
        <div className="relative">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-300 mb-2">
            <span>{isEs ? "Paso" : isEn ? "Step" : "Etapa"} {Math.min(step + 1, 3)} / 3</span>
            <button /* design-system: ALLOW legacy-todo */
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1 text-slate-400 hover:text-emerald-300 transition-colors"
              data-testid="quiz-reset"
            >
              <RotateCcw className="h-3 w-3" /> {isEs ? "Reiniciar" : isEn ? "Reset" : "Reiniciar"}
            </button>
          </div>
          <div className="h-1 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-emerald-400 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question step */}
        {step < 3 && (
          <div key={step} className="relative mt-7 lf-fade-in-up">
            <h3 className="font-serif-display text-2xl sm:text-3xl leading-snug font-medium" data-testid={`quiz-question-${step}`}>
              {set[step].q}
            </h3>
            <div className="mt-6 grid sm:grid-cols-2 gap-2.5">
              {set[step].options.map((opt, i) => (
                <button /* design-system: ALLOW legacy-todo */
                  key={i}
                  type="button"
                  onClick={() => pickOption(opt)}
                  data-testid={`quiz-option-${step}-${i}`}
                  className="group text-left bg-white/5 border border-white/10 hover:border-emerald-400/50 hover:bg-emerald-500/10 rounded-2xl px-4 py-3.5 transition-all duration-200 flex items-center gap-3"
                >
                  <span className="h-7 w-7 rounded-full border border-white/20 flex items-center justify-center shrink-0 text-[10px] font-bold font-mono-num text-slate-400 group-hover:border-emerald-400 group-hover:text-emerald-300 transition-colors">
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="text-sm text-slate-200 group-hover:text-white leading-snug">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Result step */}
        {step === 3 && result && (
          <div className="relative mt-8 lf-fade-in-up" data-testid="quiz-result">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-300">
              <Check className="h-3.5 w-3.5" />
              {isEs ? "Tu match" : isEn ? "Your match" : "Seu match"}
            </div>
            {(() => {
              const Icon = ICON_BY_SLUG[result.slug] || Briefcase;
              return (
                <div className="mt-4 flex items-center gap-4">
                  <div className="h-14 w-14 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center shrink-0">
                    <Icon className="h-7 w-7 text-emerald-300" strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="font-serif-display text-2xl sm:text-3xl leading-tight font-medium">
                      {labels[result.slug]}
                    </div>
                    <div className="text-sm text-slate-400 mt-1">
                      <Sparkles className="inline-block h-3 w-3 text-emerald-400 mr-1 -mt-0.5" />
                      {isEs
                        ? `Score ${result.score} · top entre 8 perfiles`
                        : isEn
                        ? `Score ${result.score} · top across 8 personas`
                        : `Score ${result.score} · top entre 8 perfis`}
                    </div>
                  </div>
                </div>
              );
            })()}

            <p className="mt-6 text-slate-300 leading-relaxed text-sm sm:text-base">
              {isEs
                ? "Hemos hecho una landing específica para ti. Ve cómo Lumfi se calibra para tu flujo."
                : isEn
                ? "We have a landing built for you. See how Lumfi calibrates to your flow."
                : "Temos uma landing feita para você. Veja como Lumfi se calibra para o seu fluxo."}
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to={`/for/${result.slug}`} data-testid="quiz-cta-go">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-white text-base px-7 py-6 rounded-full gap-2 shadow-[0_18px_48px_-12px_rgba(16,185,129,0.55)] hover:translate-y-[-2px] transition-all"
                >
                  {isEs ? "Ver mi landing" : isEn ? "See my landing" : "Ver minha landing"}
                  <ArrowUpRight className="h-5 w-5" />
                </Button>
              </Link>
              <button /* design-system: ALLOW legacy-todo */
                type="button"
                onClick={reset}
                data-testid="quiz-cta-retry"
                className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-300 transition-colors self-center sm:px-3"
              >
                <RotateCcw className="h-4 w-4" />
                {isEs ? "Rehacer las 3 preguntas" : isEn ? "Retake the 3 questions" : "Refazer as 3 perguntas"}
              </button>
            </div>
          </div>
        )}

        {/* Secondary CTA — keeps the user moving even before result */}
        {step === 0 && (
          <p className="relative mt-5 text-[11px] text-slate-400">
            {isEs
              ? "¿Sabes ya cuál eres? "
              : isEn
              ? "Already know which one? "
              : "Já sabe qual? "}
            <a href="#archetypes" className="text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline inline-flex items-center gap-0.5">
              {isEs ? "Saltar al listado" : isEn ? "Skip to the list" : "Pular para a lista"} <ArrowRight className="h-3 w-3" />
            </a>
          </p>
        )}
      </div>
    </section>
  );
}
